import { getEhrLaunchRegion } from '@/atoms/ehrLaunchRegionAtom';
import {
  useCurrentKindePropertiesQueryResult,
  type QueryUserKindeProperties,
} from '@/context/kinde-properties-context';
import { toValidRegion } from '@/helpers/toValidRegion';
import { getKindeUserProperties } from '@/pages/api/scribeAPI';
import { store, type RootState } from '@/store';
import { toast } from '@heidi/design-system/toast';
// `AuthProvider` is imported from its defining module rather than the
// `@/models/authModel` re-export on purpose. Pulling the auth model in for a
// plain enum makes `authModel` initialise before `@/store`, which trips the
// pre-existing `authModel` <-> `store` cycle (`store` -> `models/index` ->
// `auth`) with a TDZ ReferenceError. Only the import sort order was hiding it.
import { AuthProvider } from '@/utils/auth/persistedAuthProvider';
import { isKeycloak } from '@/utils/buildProfile';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { Region } from '@heidi/schema/ml-scribe';
import { queryOptions, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useRouter } from 'next/router';
import posthog from 'posthog-js';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { isUserQueryBlockedRoute } from './user-query-blocked-routes';

// paths
export const GET_KINDE_PROPERTIES = 'getKindeProperties';

/**
 * Reads the AUTHENTICATED region from the React Query cache for non-React
 * callers. Colocated with GET_KINDE_PROPERTIES + QueryUserKindeProperties so the
 * cache key, the result type, and this read can't drift apart — the single
 * source shared by the updater path (`feed.ts`) and the telemetry path
 * (`analyticsScribeModel` `getDesktopUpdateChannel`). NEVER `getRegion()` /
 * `useRegion()`, which fail open to AU and honour `?region=` (C10).
 */
export function getCachedKindeRegion(): Region | undefined {
  return reactQueryClient.getQueryData<QueryUserKindeProperties>([GET_KINDE_PROPERTIES])
    ?.region;
}

export const invalidateKindeProperties = async () => {
  try {
    await reactQueryClient.invalidateQueries({
      queryKey: [GET_KINDE_PROPERTIES],
    });
  } catch (error) {
    logger.warn('Failed to invalidate Kinde properties query:', error);
  }
};

export const KINDE_PROPERTIES_MAX_RETRIES = 3;
const MAX_RETRY_DELAY_MS = 30_000;
const MAX_RETRY_AFTER_MS = 60_000;

export const parseRetryAfterMs = (error: unknown): number | undefined => {
  if (!isAxiosError(error)) return undefined;
  const headerValue: unknown = error.response?.headers?.['retry-after'];
  if (typeof headerValue !== 'string' && typeof headerValue !== 'number') {
    return undefined;
  }
  const raw = String(headerValue).trim();
  if (!raw) return undefined;

  const seconds = Number(raw);
  if (Number.isFinite(seconds)) {
    return Math.max(0, seconds * 1000);
  }

  const date = Date.parse(raw);
  if (Number.isFinite(date)) {
    return Math.max(0, date - Date.now());
  }

  return undefined;
};

export const kindePropertiesShouldRetry = (
  failureCount: number,
  error: unknown
): boolean => {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    if (status === 401 || status === 403) return false;
  }
  return failureCount < KINDE_PROPERTIES_MAX_RETRIES;
};

export const kindePropertiesRetryDelay = (attempt: number, error: unknown): number => {
  const retryAfterMs = parseRetryAfterMs(error);
  if (retryAfterMs !== undefined) {
    return Math.min(retryAfterMs, MAX_RETRY_AFTER_MS);
  }
  return Math.min(1000 * 2 ** attempt, MAX_RETRY_DELAY_MS);
};

export const getKindePropertiesQueryOptions = (
  token: string | undefined,
  pathname: string
) =>
  queryOptions<QueryUserKindeProperties, Error>({
    // Route-gate so this query does not auto-fetch on pre-auth launch handoffs
    // (e.g. SMART/nCAS, Haiku). It is reached unconditionally via
    // `useQueryUserForContext` -> `HeidiIntlProvider` even under MinimalProviders,
    // and would otherwise fire with the stale rehydrated token during the launch
    // bootstrap window -> 401 -> forced logout.
    enabled: !!token && !isUserQueryBlockedRoute(pathname),
    queryKey: [GET_KINDE_PROPERTIES],
    staleTime: Number.POSITIVE_INFINITY,
    retry: kindePropertiesShouldRetry,
    retryDelay: kindePropertiesRetryDelay,
    queryFn: async (): Promise<QueryUserKindeProperties> => {
      const { authProvider, userRegion } = store.getState().auth;

      // The EHR-launch carrier first, then the slice — the same order
      // `getRegion()` uses. `auth.userRegion` is no longer persisted, so after a
      // reload the slice field is `undefined` and the carrier is the only thing
      // that still knows which region the launch named.
      //
      // Resolved ONCE, above both early returns. The `isKeycloak` branch below
      // returns before the FHIR branch, and on-prem nothing else writes
      // `auth.userRegion` at all — `AuthTokenResolver` is unreachable past
      // `auth-wrapper.tsx`, the model has no default, and there is no
      // `NEXT_PUBLIC_REGION` — so reading the slice alone there sent an on-prem
      // EHR launch to AU at `staleTime: Infinity`, which is the FHIR failure
      // relocated one branch up.
      const launchOrSliceRegion = getEhrLaunchRegion() ?? userRegion;

      // On-prem (Keycloak) is a single-region deployment — there is no
      // Kinde / Auth0 user-properties store to query. Return the
      // Redux-resident region (set via env at deploy time) or fall back
      // to AU. Skipping the call avoids a guaranteed 400 from the BE
      // endpoint, which can't resolve a Kinde user id for a Keycloak
      // token.
      if (isKeycloak) {
        return {
          region: launchOrSliceRegion || Region.AU,
        };
      }

      // Skip Kinde API call for FHIR auth provider
      if (authProvider === AuthProvider.FHIR) {
        // For FHIR, we set the region as part of the launch url or use default.
        //
        // Reading the slice alone here would send PostHog's region from 'US' to
        // undefined and this result from US to AU — and at `staleTime: Infinity`
        // that AU is then what `useIsUserRegionSameAsKindeRegion`,
        // `useEvidenceAccess`, `useRequireRegionGuard` and
        // `openfeature-context-sync` see for the rest of the session. A FHIR
        // token carries no region claim, so nothing behind this recovers it.
        posthog.setPersonPropertiesForFlags({ region: launchOrSliceRegion });

        if (!launchOrSliceRegion) {
          logger.info('FHIR auth: falling back to default region AU');
        }

        return {
          region: launchOrSliceRegion || Region.AU,
        };
      }

      const res = await getKindeUserProperties();

      const rawRegion = res.data.properties.find((prop) => prop.key === 'region')
        ?.value as string | undefined;
      const region = toValidRegion(rawRegion);

      const epicUserId = res.data.properties.find((prop) => prop.key === 'epicuserid')
        ?.value as string | null;
      const connectionId = res.data.properties.find((prop) => prop.key === 'connectionid')
        ?.value as string | null;

      posthog.setPersonPropertiesForFlags({ region: region });

      return {
        region: region,
        // Raw region string from Kinde *before* the strict enum allowlist in
        // `toValidRegion`. Lets consumers tell "no region set at all" apart
        // from "region present but unrecognised by the FE enum" (BE/FE drift),
        // so they don't treat a drifted-but-onboarded user as region-less.
        rawRegion: rawRegion,
        epicUserId: epicUserId,
        connectionId: connectionId,
      };
    },
  });

export const useKindePropertiesErrorToast = (
  query: Pick<UseQueryResult<QueryUserKindeProperties, unknown>, 'isError' | 'error'>
) => {
  const lastToastedErrorRef = useRef<unknown>(null);

  // Toast once per terminal failure, keyed on error identity to avoid retry spam.
  useEffect(() => {
    if (!query.isError || !query.error) return;
    if (lastToastedErrorRef.current === query.error) return;
    lastToastedErrorRef.current = query.error;

    logger.debug('getting kinde properties error', query.error);

    if (isAxiosError(query.error) && query.error.response) {
      toast({
        tone: 'destructive',
        title: 'Error',
        description: query.error.message,
      });
    }
  }, [query.isError, query.error]);
};

// Re-throws on failure (instead of swallowing) so `isError` is reliable for `NetworkErrorModal`.
export function useQueryKindePropertiesForContext() {
  const { token } = useSelector((state: RootState) => state.auth);
  const router = useRouter();
  const query = useQuery(getKindePropertiesQueryOptions(token, router.pathname));
  useKindePropertiesErrorToast(query);
  return query;
}

export function useQueryKindeProperties() {
  return useCurrentKindePropertiesQueryResult();
}
